import React from 'react'
import { Row, Col, Card, Statistic, Table, Progress, Empty } from 'antd'
import { PieChart, Pie, ResponsiveContainer, Cell, Legend } from 'recharts'
import dayjs from 'dayjs'
import useReportsData from '../../../hooks/useReportsData'
import { formatCurrency } from '../../../../../utils/formatters'
import styles from './Overview.module.css'

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2']

const OverviewTab = ({ dateRange }) => {
  const { overviewData, categoryAnalysis } = useReportsData(dateRange)
  const { totalValue, totalItems, itemsNeedingReorder, expiringItems } = overviewData

  // Format total value without the R prefix since Statistic component adds it
  const formattedTotalValue = totalValue.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  const getStockLevelInfo = (currentStock, minimumStock) => {
    // Calculate how much stock we have above/below the minimum
    const stockDelta = currentStock - minimumStock
    const percentageOfMin = (currentStock / minimumStock) * 100

    // If we're at or below minimum, show how far below minimum we are
    if (stockDelta <= 0) {
      return {
        percentage: percentageOfMin,
        color: '#ff4d4f', // red
        status: 'exception',
        label: `${Math.abs(stockDelta)} units below minimum`
      }
    }

    // If we're above minimum but within 25% of it
    if (percentageOfMin <= 125) {
      return {
        percentage: percentageOfMin,
        color: '#faad14', // orange
        status: 'normal',
        label: `${stockDelta} units above minimum`
      }
    }

    // Otherwise we're well above minimum
    return {
      percentage: 100,
      color: '#52c41a', // green
      status: 'success',
      label: `${stockDelta} units above minimum`
    }
  }

  return (
    <div className={styles.overviewContainer}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={6}>
          <Card className={styles.statsCard}>
            <Statistic
              title='Total Inventory Value'
              value={formattedTotalValue}
              prefix='R'
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Card className={styles.statsCard}>
            <Statistic title='Total Items' value={totalItems} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Card className={styles.statsCard}>
            <Statistic
              title='Items Needing Reorder'
              value={itemsNeedingReorder.length}
              valueStyle={{ color: itemsNeedingReorder.length > 0 ? '#ff4d4f' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Card className={styles.statsCard}>
            <Statistic
              title='Items Expiring Soon'
              value={expiringItems.length}
              valueStyle={{ color: expiringItems.length > 0 ? '#faad14' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className={styles.chartRow}>
        <Col xs={24} lg={12}>
          <Card title='Inventory by Category' className={styles.overviewChartCard}>
            <ResponsiveContainer width='100%' height={300}>
              <PieChart>
                <Pie
                  data={categoryAnalysis}
                  cx='50%'
                  cy='50%'
                  labelLine={false}
                  outerRadius={100}
                  fill='#8884d8'
                  dataKey='itemCount'
                  nameKey='name'
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {categoryAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title='Items with Low Stock' className={styles.overviewTableCard}>
            {itemsNeedingReorder.length > 0 ? (
              <Table
                dataSource={itemsNeedingReorder.slice(0, 5)}
                columns={[
                  { title: 'Item Name', dataIndex: 'name', key: 'name' },
                  {
                    title: 'Stock Level',
                    key: 'stockLevel',
                    render: (_, record) => (
                      <div style={{ minWidth: '200px' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <span>Current: {record.currentStock}</span>
                          <span>Minimum: {record.minimumStock}</span>
                        </div>
                        {(() => {
                          const stockInfo = getStockLevelInfo(record.currentStock || 0, record.minimumStock || 0)
                          return (
                            <>
                              <Progress
                                percent={stockInfo.percentage}
                                size='small'
                                status={stockInfo.status}
                                strokeColor={stockInfo.color}
                              />
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: stockInfo.color,
                                  marginTop: '4px'
                                }}
                              >
                                {stockInfo.label}
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    )
                  }
                ]}
                pagination={false}
                size='small'
              />
            ) : (
              <Empty description='No items need reordering' />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className={styles.tableRow}>
        <Col span={24}>
          <Card title='Items Expiring Soon' className={styles.overviewTableCard}>
            {expiringItems.length > 0 ? (
              <Table
                dataSource={expiringItems.slice(0, 5)}
                columns={[
                  { title: 'Item Name', dataIndex: 'name', key: 'name' },
                  { title: 'Category', dataIndex: 'category', key: 'category' },
                  {
                    title: 'Expiry Date',
                    dataIndex: 'expiryDate',
                    key: 'expiryDate',
                    render: (date) => dayjs(date).format('MMM D, YYYY')
                  },
                  {
                    title: 'Current Stock',
                    key: 'currentStock',
                    render: (_, record) => record.currentStock || 0
                  },
                  {
                    title: 'Days Until Expiry',
                    key: 'daysUntilExpiry',
                    render: (_, record) => {
                      const days = dayjs(record.expiryDate).diff(dayjs(), 'day')
                      let color = days < 30 ? '#ff4d4f' : days < 60 ? '#faad14' : '#52c41a'
                      return <span style={{ color }}>{days} days</span>
                    }
                  }
                ]}
                pagination={false}
                size='small'
              />
            ) : (
              <Empty description='No items are expiring soon' />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default OverviewTab
